/**
 * useKeepSessionAlive
 *
 * Holds the session open while an UNATTENDED display is genuinely on screen.
 *
 * Built for TV Mode: the jobs board is put on a wall in the workshop and left
 * running all day with nobody touching the machine. useSessionManager's whole
 * job is to log out after 15 minutes of no mouse/keyboard/scroll — which is
 * exactly right for a desk, and exactly wrong for a wall display. The board
 * would show "Still there? You'll be signed out in 5 seconds", get no answer
 * because there is no keyboard in front of it, and drop to the login screen.
 *
 * HOW, AND WHY THIS WAY
 * ---------------------
 * It refreshes the same shared activity timestamp the real input listeners
 * write (LAST_ACTIVITY_KEY in localStorage) rather than adding a "suppress the
 * modal" flag. That one write covers all four behaviours that would otherwise
 * need handling separately:
 *
 *   1. the warning modal never opens          (idleMs stays below WARNING_AT_MS)
 *   2. the auto-logout never fires            (idleMs stays below IDLE_LIMIT_MS)
 *   3. the PROACTIVE TOKEN REFRESH keeps running — this is the one that is easy
 *      to miss. maybeRefresh() bails out when idleMs >= IDLE_LIMIT_MS, so a flag
 *      that only hid the modal would leave the board logged in but holding a
 *      token that expires in an hour, and the display would die of 401s instead
 *      of a logout. Slower to notice, no less broken.
 *   4. other tabs stay alive too, because the timestamp is shared.
 *
 * ⚠️ Point 4 is a deliberate trade-off, not an accident: a board running in TV
 *    Mode keeps every other tab on that browser signed in as well. That falls
 *    out of the existing cross-tab design, where activity anywhere counts as
 *    activity everywhere. Worth knowing before leaving TV Mode running on a
 *    machine someone also does admin work on.
 *
 * `document.hidden` is what keeps this honest. A TV Mode tab that has been
 * backgrounded — minimised, or another tab brought forward — is NOT on a wall
 * being looked at, so it stops counting as activity and the normal idle rules
 * take over. Without that check, opening TV Mode once and forgetting about it
 * would hold a session open indefinitely. This mirrors the TV Mode auto-refresh
 * in JobsPage, which skips its fetch on a hidden tab for the same reason.
 */

import { useEffect } from 'react';

import { setLastActivity } from '../services/authService';

// Comfortably inside the 14-minute warning threshold, and far cheaper than the
// TV Mode poll it sits alongside — this is a single localStorage write, no
// network. Each write does raise a `storage` event in other tabs, which is why
// this is a minute rather than a few seconds.
const KEEP_ALIVE_INTERVAL_MS = 60 * 1000;

/**
 * @param {boolean} active — hold the session open while true. Pass the TV Mode
 *                           flag directly; the hook no-ops when false.
 */
export default function useKeepSessionAlive(active) {
    useEffect(() => {
        if (!active) return undefined;

        const beat = () => {
            // Hidden tab => not on display => not activity. See the note above.
            if (!document.hidden) setLastActivity();
        };

        // Fire once immediately: entering TV Mode should reset the clock rather
        // than inherit however idle the session already was.
        beat();

        const id = setInterval(beat, KEEP_ALIVE_INTERVAL_MS);
        return () => clearInterval(id);
    }, [active]);
}
